import React from "react";
import { ArrowLeft, Download, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "./Reveal";

/**
 * LegalPage — shared wrapper for all legal pages.
 * Embeds a PDF (Google Docs viewer) with fallback link + download button.
 */
export const LegalPage = ({ title, pdfUrl, description, testId }) => {
  const viewerUrl = pdfUrl ? `https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true` : null;

  return (
    <div data-testid={testId || "legal-page"}>
      <section className="relative pt-40 pb-16 overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 z-10">
          <Reveal>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm mb-6 hover:text-[#6B2FD9] transition-colors"
              style={{ color: "var(--text-muted)" }}
              data-testid="legal-back-link"
            >
              <ArrowLeft size={14} /> Back to home
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="text-xs uppercase tracking-[0.25em] text-[#6B2FD9] font-semibold mb-3">Legal</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter max-w-3xl text-balance" style={{ color: "var(--text)" }}>
              {title}
            </h1>
          </Reveal>
          {description && (
            <Reveal delay={0.2}>
              <p className="mt-6 text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>{description}</p>
            </Reveal>
          )}
        </div>
      </section>

      <section className="relative pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div
              className="rounded-2xl border overflow-hidden"
              style={{ backgroundColor: "var(--surface)", borderColor: "var(--border)" }}
            >
              <div
                className="flex flex-wrap items-center justify-between gap-3 p-4 md:p-5 border-b"
                style={{ borderColor: "var(--border)" }}
              >
                <div className="text-sm font-medium" style={{ color: "var(--text)" }}>{title} — Document</div>
                <div className="flex gap-2">
                  {pdfUrl && (
                    <>
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline-zl inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
                        data-testid="legal-open-btn"
                      >
                        <ExternalLink size={12} /> Open
                      </a>
                      <a
                        href={pdfUrl}
                        download
                        className="btn-primary-zl inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
                        data-testid="legal-download-btn"
                      >
                        <Download size={12} /> Download
                      </a>
                    </>
                  )}
                </div>
              </div>

              {pdfUrl ? (
                <iframe
                  src={viewerUrl}
                  title={title}
                  className="w-full h-[80vh] bg-white"
                  data-testid="legal-pdf-viewer"
                />
              ) : (
                <div className="p-12 md:p-16 text-center" style={{ color: "var(--text-muted)" }}>
                  <div className="w-14 h-14 rounded-full bg-[#6B2FD9]/15 border border-[#6B2FD9]/30 flex items-center justify-center mx-auto text-[#8A4FFF] mb-4">
                    <Download size={22} />
                  </div>
                  <p className="text-base">Document coming soon.</p>
                  <p className="text-sm mt-2">Please check back shortly or contact us at hello@zelaflo.com.</p>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;
